import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: #0A0A0A;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
`;

const LogoWrap = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.8rem;
`;

const LogoImg = styled(motion.img)`
  height: 70px;
  width: auto;
  background: #fff;
  border-radius: 10px;
  padding: 6px 14px;
`;

const TaglineWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const TDot = styled(motion.span)`
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #F47920;
`;

const Tagline = styled(motion.p)`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #555;
`;

const BarWrap = styled.div`
  width: 220px;
  height: 2px;
  background: #1A1A1A;
  border-radius: 2px;
  overflow: hidden;
`;

const Bar = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #C5601A, #F47920, #FF9A45);
  border-radius: 2px;
  transform-origin: left;
`;

const PercentText = styled(motion.div)`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: #333;
`;

const CornerTag = styled(motion.div)`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #2A2A2A;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 0.55rem;
    white-space: normal;
    text-align: center;
    width: 80%;
    justify-content: center;
  }
`;

export default function LoadingScreen({ onFinish }) {
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    const duration = 2200;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const p = Math.min(Math.round((elapsed / duration) * 100), 100);
      setPercent(p);
      if (p < 100) {
        requestAnimationFrame(tick);
      } else {
        setTimeout(onFinish, 500);
      }
    };

    requestAnimationFrame(tick);
  }, [onFinish]);

  return (
    <Overlay
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.04 }}
      transition={{ duration: 0.55, ease: 'easeInOut' }}
    >
      <LogoWrap
        initial={{ opacity: 0, scale: 0.75, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <LogoImg
          src="/img/logo/logo.jpeg"
          alt="Carnes Fereima"
        />

        <TaglineWrap>
          <TDot
            animate={{ opacity: [1, 0.2, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, delay: 0 }}
          />
          <Tagline
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            Del Campo a tu Mesa
          </Tagline>
          <TDot
            animate={{ opacity: [1, 0.2, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, delay: 0.7 }}
          />
        </TaglineWrap>
      </LogoWrap>

      {/* barra de progreso */}
      <motion.div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.4 }}
      >
        <BarWrap>
          <Bar
            initial={{ scaleX: 0 }}
            animate={{ scaleX: percent / 100 }}
            transition={{ duration: 0.1, ease: 'linear' }}
          />
        </BarWrap>
        <PercentText>{percent}%</PercentText>
      </motion.div>

      <CornerTag
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        San Juan de los Lagos, Jalisco · Más de 20 Años
      </CornerTag>
    </Overlay>
  );
}
